import { useState, useEffect } from 'react';
import { hotelApi } from '../../hotelApi';

const ROLES = [
  { value: 'admin',       label: 'Admin',       desc: 'Full access — all operations', color: '#137333', bg: '#e6f4ea' },
  { value: 'manager',     label: 'Manager',     desc: 'Manage orders, billing, inventory', color: '#b45309', bg: '#fef3c7' },
  { value: 'waiter',      label: 'Waiter',      desc: 'Take orders and serve tables', color: '#1a73e8', bg: '#e8f0fe' },
  { value: 'cook',        label: 'Cook',        desc: 'Kitchen access — update item status', color: '#e65100', bg: '#fff3e0' },
  { value: 'receptionist',label: 'Receptionist',desc: 'Bookings and front desk', color: '#6a1b9a', bg: '#f3e5f5' },
  { value: 'staff',       label: 'Staff',       desc: 'General read/write access', color: '#546e7a', bg: '#f1f3f4' },
];

const EMPTY = { name: '', username: '', phone: '', role: 'waiter', isActive: true, notes: '', password: '' };

function roleBadge(r) {
  const found = ROLES.find(x => x.value === r) || ROLES[ROLES.length - 1];
  return <span style={{ background: found.bg, color: found.color, borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>{found.label}</span>;
}

export default function HotelStaff() {
  const [staff, setStaff]     = useState([]);
  const [form, setForm]       = useState(EMPTY);
  const [editId, setEditId]   = useState(null);
  const [tab, setTab]         = useState('list');
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');
  const [saving, setSaving]   = useState(false);

  const load = () => {
    setLoading(true);
    hotelApi.getStaff().then(rows => setStaff(rows || [])).catch(() => setStaff([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    try {
      if (editId) await hotelApi.updateStaff(editId, form);
      else await hotelApi.createStaff(form);
      setForm(EMPTY); setEditId(null); setTab('list'); load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleEdit = (u) => {
    setForm({
      name: u.name || '', username: u.username || '', phone: u.phone || '',
      role: u.role || 'waiter', isActive: u.is_active !== 0, notes: u.notes || '', password: ''
    });
    setEditId(u.id); setTab('form');
  };

  const handleDeactivate = async (id, name) => {
    if (!window.confirm(`Deactivate "${name}"?`)) return;
    await hotelApi.deleteStaff(id); load();
  };

  if (loading) return <div className="loading-msg">Loading staff…</div>;

  return (
    <div>
      <div className="tabs">
        <button className={`tab${tab === 'list' ? ' active' : ''}`}
          onClick={() => { setTab('list'); setEditId(null); setForm(EMPTY); }}>
          👥 Staff ({staff.length})
        </button>
        <button className={`tab${tab === 'form' ? ' active' : ''}`} onClick={() => setTab('form')}>
          {editId ? '✏️ Edit Staff' : '➕ Add Staff'}
        </button>
      </div>

      {tab === 'list' && (
        <div>
          {/* Role legend */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            {ROLES.map(r => (
              <div key={r.value} style={{ background: r.bg, border: `1px solid ${r.color}30`,
                borderRadius: 8, padding: '7px 12px', fontSize: 12 }}>
                <span style={{ color: r.color, fontWeight: 700 }}>{r.label}</span>
                <span style={{ color: '#5f6368', marginLeft: 8 }}>{r.desc}</span>
              </div>
            ))}
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Hotel Staff</div>
              <button className="btn btn-primary" style={{ padding: '5px 16px', fontSize: 13 }}
                onClick={() => { setForm(EMPTY); setEditId(null); setTab('form'); }}>
                ➕ Add Staff
              </button>
            </div>
            <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Username</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Since</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {staff.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', padding: 32 }}>
                      No staff registered yet
                    </td></tr>
                  )}
                  {staff.map(u => (
                    <tr key={u.id}>
                      <td><strong>{u.name}</strong></td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{u.username || '—'}</td>
                      <td style={{ fontSize: 12 }}>{u.phone || '—'}</td>
                      <td>{roleBadge(u.role)}</td>
                      <td>
                        {u.is_active
                          ? <span style={{ background: '#e6f4ea', color: '#137333', borderRadius: 12, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>Active</span>
                          : <span style={{ background: '#f1f3f4', color: '#9aa0a6', borderRadius: 12, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>Inactive</span>
                        }
                      </td>
                      <td style={{ fontSize: 12, color: '#888' }}>{u.created_at || '—'}</td>
                      <td>
                        <button className="btn-sm btn-secondary" onClick={() => handleEdit(u)} style={{ marginRight: 4 }}>Edit</button>
                        {u.is_active
                          ? <button className="btn-sm btn-danger" onClick={() => handleDeactivate(u.id, u.name)}>Deactivate</button>
                          : <span style={{ fontSize: 12, color: '#888' }}>Inactive</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'form' && (
        <div className="card" style={{ maxWidth: 640 }}>
          <div className="card-header">
            <div className="card-title">{editId ? '✏️ Edit Staff Member' : '➕ Register New Staff'}</div>
          </div>
          <div className="card-body">
            {err && <div style={{ marginBottom: 16, padding: '8px 14px', background: '#fce8e6', color: '#c5221f', borderRadius: 6, fontSize: 13 }}>⚠️ {err}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-control" value={form.name}
                  onChange={e => set('name', e.target.value)} placeholder="e.g. Ravi Kumar" />
              </div>
              <div className="form-group">
                <label className="form-label">Login Username</label>
                <input className="form-control" value={form.username}
                  onChange={e => set('username', e.target.value)} placeholder="Used for login" />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-control" value={form.phone}
                  onChange={e => set('phone', e.target.value)} placeholder="+91 9XXXXXXXXX" />
              </div>
              <div className="form-group">
                <label className="form-label">Role *</label>
                <select className="form-control" value={form.role}
                  onChange={e => set('role', e.target.value)}>
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{editId ? 'New Password' : 'Password'}</label>
                <input className="form-control" type="password" value={form.password}
                  onChange={e => set('password', e.target.value)}
                  placeholder={editId ? 'Leave blank to keep current' : 'Set login password'} />
                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                  Used for staff login portal
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <input className="form-control" value={form.notes}
                  onChange={e => set('notes', e.target.value)} placeholder="Optional" />
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" id="isActive" checked={form.isActive}
                  onChange={e => set('isActive', e.target.checked)} style={{ width: 16, height: 16 }} />
                <label htmlFor="isActive" className="form-label" style={{ margin: 0 }}>Active (can log in)</label>
              </div>
            </div>

            {/* Role explanation */}
            {form.role && (
              <div style={{ marginTop: 14, background: ROLES.find(r => r.value === form.role)?.bg || '#f8f9fa',
                borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
                <strong style={{ color: ROLES.find(r => r.value === form.role)?.color }}>
                  {ROLES.find(r => r.value === form.role)?.label} Role:
                </strong>
                <div style={{ marginTop: 4, color: '#444' }}>
                  {ROLES.find(r => r.value === form.role)?.desc}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? '💾 Update' : '➕ Register'}
              </button>
              <button className="btn btn-secondary"
                onClick={() => { setTab('list'); setEditId(null); setForm(EMPTY); setErr(''); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
